import {
  estimatePaidSignups,
  PAID_BUDGET_MODE_CUSTOM,
  PAID_BUDGET_MODE_LINK,
  resolvePaidBudgetMo,
} from '../model/channels';

export default function ChannelsPanel({ channels, marketingBudgetMo = 0, onChange }) {
  const update = (patch) => onChange({ ...channels, ...patch });

  const updateOrganic = (patch) =>
    update({ organic: { ...channels.organic, ...patch } });

  const updatePaid = (patch) => update({ paid: { ...channels.paid, ...patch } });

  const updateReferral = (patch) =>
    update({ referral: { ...channels.referral, ...patch } });

  const budgetMode =
    channels.paid?.budgetMode === PAID_BUDGET_MODE_CUSTOM
      ? PAID_BUDGET_MODE_CUSTOM
      : PAID_BUDGET_MODE_LINK;
  const linked = budgetMode === PAID_BUDGET_MODE_LINK;
  const effectiveBudget = resolvePaidBudgetMo(
    { marketingBudgetMo },
    channels.paid
  );
  const impliedSignups = estimatePaidSignups(effectiveBudget, channels.paid?.cac);

  const setBudgetMode = (mode) => {
    if (mode === PAID_BUDGET_MODE_LINK) {
      updatePaid({
        budgetMode: PAID_BUDGET_MODE_LINK,
        budgetMo: marketingBudgetMo || 0,
      });
      return;
    }
    updatePaid({
      budgetMode: PAID_BUDGET_MODE_CUSTOM,
      budgetMo: channels.paid?.budgetMo ?? marketingBudgetMo ?? 0,
    });
  };

  return (
    <section className="chart-card economics-panel">
      <h2>Acquisition channels</h2>
      <p className="chart-hint">
        <strong>Paid</strong> = budget ÷ CAC (Google / Reddit / LinkedIn —{' '}
        <strong>not Meta Ads</strong>). Default links paid budget to Assumptions marketing phases.{' '}
        <strong>Organic</strong> = SEO + organic FB/IG posts (Growth presets — not scaled by ad
        spend).
      </p>

      <label className="economics-field forecast-toggle">
        <input
          type="checkbox"
          checked={channels.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <span>Enable channel model (overrides base signups)</span>
      </label>

      <h3 className="economics-subheading">Organic</h3>
      <p className="chart-hint economics-muted">
        Independent of marketing budget. Phased Growth options override Growth %/mo when enabled.
      </p>
      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Mode</span>
          <select
            value={channels.organic?.mode || 'growth'}
            onChange={(e) => updateOrganic({ mode: e.target.value })}
          >
            <option value="absolute">Absolute monthly</option>
            <option value="growth">Growth from prior month</option>
          </select>
        </label>
        <label className="economics-field">
          <span>Monthly signups</span>
          <input
            type="number"
            min="0"
            value={channels.organic?.monthlySignups ?? 0}
            onChange={(e) =>
              updateOrganic({ monthlySignups: Math.max(0, parseFloat(e.target.value) || 0) })
            }
          />
        </label>
        <label className="economics-field">
          <span>Growth % / mo</span>
          <input
            type="number"
            min="-50"
            max="100"
            value={channels.organic?.growthPctMo ?? 0}
            onChange={(e) =>
              updateOrganic({
                growthPctMo: Math.min(100, Math.max(-50, parseFloat(e.target.value) || 0)),
              })
            }
            disabled={channels.organic?.mode === 'absolute'}
          />
        </label>
      </div>

      <h3 className="economics-subheading">Paid</h3>
      <label className="economics-field forecast-toggle">
        <input
          type="checkbox"
          checked={channels.paid?.enabled}
          onChange={(e) => updatePaid({ enabled: e.target.checked })}
        />
        <span>Include paid acquisition</span>
      </label>
      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Budget source</span>
          <select
            value={budgetMode}
            onChange={(e) => setBudgetMode(e.target.value)}
            disabled={!channels.paid?.enabled}
          >
            <option value={PAID_BUDGET_MODE_LINK}>
              Link to marketing phases (default)
            </option>
            <option value={PAID_BUDGET_MODE_CUSTOM}>Custom paid budget</option>
          </select>
        </label>
        <label className="economics-field">
          <span>Initial monthly budget ($)</span>
          <input
            type="number"
            min="0"
            value={linked ? marketingBudgetMo || 0 : channels.paid?.budgetMo ?? 0}
            onChange={(e) => {
              const value = Math.max(0, parseFloat(e.target.value) || 0);
              if (linked) return;
              updatePaid({ budgetMo: value, budgetMode: PAID_BUDGET_MODE_CUSTOM });
            }}
            disabled={!channels.paid?.enabled || linked}
            title={
              linked
                ? 'Linked — edit Marketing budget on Assumptions'
                : 'Custom acquisition spend'
            }
          />
          <span className="economics-muted">
            {linked
              ? 'Synced from Assumptions → marketing phases (per month)'
              : 'Acquisition only (cash still uses marketing phases)'}
          </span>
        </label>
        <label className="economics-field">
          <span>CAC ($)</span>
          <input
            type="number"
            min="1"
            value={channels.paid?.cac ?? 1}
            onChange={(e) =>
              updatePaid({ cac: Math.max(1, parseFloat(e.target.value) || 1) })
            }
            disabled={!channels.paid?.enabled}
          />
        </label>
      </div>
      {channels.paid?.enabled ? (
        <p className="chart-hint">
          Initial implied paid signups ≈ <strong>{impliedSignups.toFixed(1)}/mo</strong> ($
          {effectiveBudget.toFixed(0)} ÷ ${Number(channels.paid?.cac || 1).toFixed(0)} CAC)
          {linked ? '' : ' · cash opex still uses Assumptions marketing'}
        </p>
      ) : null}

      <h3 className="economics-subheading">Referral</h3>
      <label className="economics-field forecast-toggle">
        <input
          type="checkbox"
          checked={channels.referral?.enabled}
          onChange={(e) => updateReferral({ enabled: e.target.checked })}
        />
        <span>Include referral loop</span>
      </label>
      <div className="economics-form-grid">
        <label className="economics-field">
          <span>K-factor</span>
          <input
            type="number"
            min="0"
            max="2"
            step="0.01"
            value={channels.referral?.kFactor ?? 0}
            onChange={(e) =>
              updateReferral({
                kFactor: Math.min(2, Math.max(0, parseFloat(e.target.value) || 0)),
              })
            }
          />
        </label>
        <label className="economics-field">
          <span>Lag (months)</span>
          <input
            type="number"
            min="0"
            max="6"
            value={channels.referral?.lagMonths ?? 1}
            onChange={(e) =>
              updateReferral({
                lagMonths: Math.min(6, Math.max(0, parseInt(e.target.value, 10) || 0)),
              })
            }
          />
        </label>
        <label className="economics-field">
          <span>Soft TAM cap (logos)</span>
          <input
            type="number"
            min="0"
            placeholder="None"
            value={channels.tamCap ?? ''}
            onChange={(e) =>
              update({
                tamCap:
                  e.target.value === ''
                    ? null
                    : Math.max(0, parseFloat(e.target.value) || 0),
              })
            }
          />
        </label>
      </div>
    </section>
  );
}
